import React from 'react';
import { useDispatch, connect } from 'react-redux';
import { AppBar, CircularProgress, Divider, IconButton, Link, List, ListItem, ListItemIcon, ListItemText, Popover, Toolbar, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useOktaAuth } from '@okta/okta-react';
import { Link as RouterLink } from 'react-router-dom';
import ePlusLogo from '../../assets/logo_transparent_simple.png';
import Notifications from './Notifications';
import Toast from '../../components/Toast';
import RightDrawer from '../../components/RightDrawer';
import { openDrawer } from '../../app/actions/UiAction';
import { useLazyGetCurrentUserQuery } from '../../app/DataProtectionApi';
import { userSignin, userSignout } from '../../app/actions/User';
import Navbar from './components/Navbar';

const drawerWidth = 250;

const useStyles = makeStyles((theme) => ({
    '@global': {
        '::-webkit-scrollbar': {
            width: '1.4em',
            height: '1.4em',
        },
        '::-webkit-scrollbar-thumb': {
            borderRadius: 7,
            borderWidth: 3,
            borderColor: 'rgba(0,0,0,0)',
            borderStyle: 'solid',
            background: 'rgba(0,0,0,0.4)',
            backgroundClip: 'padding-box',
            '&:hover': {
                background: 'rgba(0,0,0,0.7)',
                backgroundClip: 'padding-box',
            },
        },
    },
    root: {
        display: 'flex',
        height: '100%',
        backgroundColor: '#eff3f8',
        // backgroundColor: theme.palette.background.paper,
        // overflowY: 'auto',
        // paddingBottom: theme.spacing(6),
    },
    offset: theme.mixins.toolbar,
    menuButton: {
        marginRight: theme.spacing(1),
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.background.paper,
        height: '65px',
        width: '100%',
    },
    appBarShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    },
    hide: {
        display: 'none',
    },
    drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        zIndex: 1000,
    },
    drawerOpen: {
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        overflowX: 'hidden',
    },
    drawerClose: {
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: `calc(${theme.spacing(6)} + 1px)`,
        [theme.breakpoints.up('sm')]: {
            width: `calc(${theme.spacing(7)} + 1px)`,
        },
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: theme.spacing(0, 1),
        // necessary for content to be below app bar
        ...theme.mixins.toolbar,
    },
    content: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        width: `calc(100% - ${drawerWidth}px)`,
        backgroundColor: '#eff3f8',
    },
    innerContent: {
        flexGrow: 1,
        padding: theme.spacing(3),
        // height of appbar
        height: `calc(100vh - 114px)`,
        // causing double scroll bar
        // minHeight: 'calc(100vh - 160px)',
        overflowY: 'auto',
    },
    logo: {
        height: '42px',
        padding: theme.spacing(0, 2, 0, 1),
    },
    name: {
        padding: 16,
    },
    bottomButtons: {
        right: '-45px',
        bottom: '50%',
        position: 'fixed',
        transform: 'rotate(-90deg)',
    },
    chat: {
        right: '2%',
        bottom: '3%',
        position: 'fixed',
    },
}));

const Base = ({ children, currentUser }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const [open, setOpen] = React.useState(true);
    const [userInfo, setUserInfo] = React.useState(null);
    const { authState, oktaAuth } = useOktaAuth();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const openUserMenu = Boolean(anchorEl);

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // const openTicketDrawer = () => {
    //     dispatch(openDrawer(<Ticket />));
    // };

    const openNotificationsDrawer = () => {
        dispatch(openDrawer({ rightDrawerContent: <Notifications /> }));
    };

    // const openFeedbackDrawer = () => {
    //     dispatch(openDrawer(<Feedback />));
    // };

    // const handleDrawer = () => {
    //     dispatch(closeDrawer());
    // };

    const handleLogout = async () => {
        await oktaAuth.signOut();
    };

    // TODO: Switch to redux and maybe saga

    React.useEffect(() => {
        if (!authState || !authState.isAuthenticated) {
            // When user isn't authenticated, forget any user info
            setUserInfo(null);
        } else {
            oktaAuth.getUser().then((info) => {
                setUserInfo(info);
            });
        }
    }, [authState, oktaAuth]);

    const [getCurrentUser] = useLazyGetCurrentUserQuery();

    React.useEffect(() => {
        if (!userInfo) {
            // When user isn't authenticated, forget any user info
            dispatch(userSignout());
        } else {
            getCurrentUser()
                .unwrap()
                .then((userData) => dispatch(userSignin(userData)));
        }
    }, [userInfo, dispatch, getCurrentUser]);

    // const logout = React.useCallback(async () => oktaAuth.signOut({ postLogoutRedirectUri: `${window.location.origin}/logout` }), [oktaAuth]);

    return (
        <>
            <div className={classes.root}>
                <AppBar position="fixed" className={classes.appBar}>
                    <Toolbar>
                        <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setOpen((value) => !value)}>
                            <MenuIcon />
                        </IconButton>
                        <Link component={RouterLink} to="/">
                            <img src={ePlusLogo} alt="ePlus Logo" className={classes.logo} />
                        </Link>
                        <Typography variant="h6" style={{ textDecoration: 'none', color: 'inherit', flexGrow: 1 }}>
                            ePlus Portal
                        </Typography>
                        {authState?.isAuthenticated && (
                            <div>
                                {/* <Button variant='outlined' onClick={openTicketDrawer} color='inherit' >
                                Open Ticket
                            </Button> */}
                                <IconButton aria-label="notifications for user" onClick={openNotificationsDrawer} aria-controls="menu-appbar" aria-haspopup="true" color="inherit">
                                    <NotificationsIcon />
                                </IconButton>
                                <IconButton aria-label="account of current user" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleMenu} color="inherit">
                                    <AccountCircle />
                                </IconButton>
                                <Popover
                                    id="menu-appbar"
                                    anchorEl={anchorEl}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'center',
                                    }}
                                    keepMounted
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center',
                                    }}
                                    open={openUserMenu}
                                    onClose={handleClose}>
                                    <div className={classes.toolbar}>
                                        {userInfo ? (
                                            <>
                                                <List>
                                                    <ListItem key="name">
                                                        <ListItemIcon>
                                                            <AccountCircleIcon />
                                                        </ListItemIcon>
                                                        <ListItemText primary={`${userInfo.given_name} ${userInfo.family_name}`} />
                                                    </ListItem>
                                                    <Divider />
                                                    <ListItem button key="logout" onClick={handleLogout}>
                                                        <ListItemText primary="Logout" />
                                                    </ListItem>
                                                </List>
                                            </>
                                        ) : (
                                            <CircularProgress />
                                        )}
                                    </div>
                                    <Divider />

                                    {/* <MenuItem onClick={logout}>Logout</MenuItem> */}
                                </Popover>
                            </div>
                        )}
                    </Toolbar>
                </AppBar>
                <Navbar currentUser={currentUser} open={open} />
                <Toast />
                <RightDrawer />
                <div className={classes.content}>
                    <div className={classes.offset} />
                    <div className={classes.innerContent}>{children}</div>
                </div>
            </div>
            {/* <ButtonGroup className={classes.bottomButtons}>
            <Button onClick={openFeedbackDrawer}>Feedback</Button>
        </ButtonGroup> */}
            {/* <ButtonGroup className={classes.chat}>
            <Button variant='contained'>Chat with us!</Button>
        </ButtonGroup> */}
        </>
    );
};

const mapStateToProps = (state) => {
    return { currentUser: state.User.user };
};

export default connect(mapStateToProps)(Base);
